import {useEffect, useMemo, useState} from "react";

import {IRange} from "../types/IRange";
import {RangeType} from "../types/RangeType";
import {changeValue} from "../utils/change_value";
import {getInitialValue} from "../utils/get_initial_values";

export const useValuesWithTransformation = (
    values: IRange<RangeType>,
    step: number,
    min: number,
    max: number,
    center?: number,
    useStepChange?: boolean,
    useDynamicChange?: boolean
) => {
    const middlePoint = center || max / 2;
    const [sliderValues, setSliderValues] = useState(getInitialValue(values, min));
    const [realStep, setRealStep] = useState<number>(step);

    useEffect(() => {
        const newValue = changeValue(values, min, max);
        const valueForStep = newValue && newValue[newValue.length - 1];

        if (useStepChange && valueForStep) {
            if (valueForStep < 1000000) {
                setRealStep(50000);
            }
            if (valueForStep >= 1000000 && valueForStep <= 2500000 && step !== 100000) {
                setRealStep(100000);
            }

            if (valueForStep > 2500000 && step !== 250000) {
                setRealStep(250000);
            }
        }

        if (center && newValue?.length && useDynamicChange) {
            const mappedValues = newValue.map((value) => mapToPercentValue(value));
            setSliderValues(mappedValues);
            return;
        }

        if (newValue?.length && useDynamicChange) {
            setSliderValues(newValue);
        }
    }, [values, center]);

    const mapToPercentValue = (real: number) => {
        if (real <= middlePoint) {
            return ((real - min) * 50) / (middlePoint - min);
        } else {
            return 50 + ((real - middlePoint) * 50) / (max - middlePoint);
        }
    };

    const mapToRealValue = (mappedValue: number) => {
        if (mappedValue <= 50) {
            const realValue = Math.round(min + ((middlePoint - min) * mappedValue) / 50);
            if (realValue > middlePoint - realStep && realValue < middlePoint) {
                return middlePoint;
            }
            return realValue;
        } else {
            return Math.round(middlePoint + ((max - middlePoint) * (mappedValue - 50)) / 50);
        }
    };

    const calculateMappedStep = (realStep: number) => {
        const leafStep = realStep / ((middlePoint - min) / 50);
        const rightStep = realStep / ((max - middlePoint) / 50);
        if (Math.round(sliderValues[sliderValues.length - 1]) < 50) {
            return leafStep;
        } else {
            return rightStep;
        }
    };

    const sliderStep = useMemo(() => (center ? calculateMappedStep(realStep) : realStep), [sliderValues, realStep, center]);

    return {
        sliderValues,
        sliderStep,
        minimum: center ? 0 : min,
        maximum: center ? 100 : max,
        setSliderValues,
        mapToRealValue
    };
};
